import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import {
  listCustomDomains,
  registerCustomDomain,
  removeCustomDomain,
  verifyCustomDomain,
} from '../services/api';
import './DomainsPage.css';

const DOMAIN_TYPES = [
  { id: 'menu', icon: 'restaurant_menu', labelKey: 'domains.typeMenu' },
  { id: 'comanda', icon: 'kitchen', labelKey: 'domains.typeComanda' },
  { id: 'admin', icon: 'admin_panel_settings', labelKey: 'domains.typeAdmin' },
];

function DomainsPage() {
  const { t } = useTranslation();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newHostnames, setNewHostnames] = useState({ menu: '', comanda: '', admin: '' });
  const [adding, setAdding] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [message, setMessage] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const loadDomains = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listCustomDomains();
      setDomains(data.data || data.domains || data || []);
    } catch (err) {
      console.error('Error loading domains:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const getDomainForType = (type) => domains.find((d) => (d.type || 'menu') === type);

  const handleAdd = async (type) => {
    const hostname = newHostnames[type]?.toLowerCase().trim();
    if (!hostname) return;

    const hostnameRegex = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/;
    if (!hostnameRegex.test(hostname)) {
      setMessage({ type: 'error', text: t('domains.invalidFormat') });
      return;
    }

    try {
      setAdding(type);
      setMessage(null);
      await registerCustomDomain(hostname, type);
      setNewHostnames((prev) => ({ ...prev, [type]: '' }));
      setMessage({ type: 'success', text: t('domains.addedSuccess') });
      await loadDomains();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('domains.addError') });
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (hostname) => {
    setRemoveConfirm(null);
    setMessage(null);
    try {
      await removeCustomDomain(hostname);
      setMessage({ type: 'success', text: t('domains.removedSuccess') });
      await loadDomains();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('domains.removeError') });
    }
  };

  const handleVerify = async (hostname) => {
    setMessage(null);
    setVerifying(hostname);
    try {
      const result = await verifyCustomDomain(hostname);
      if (result.verified) {
        setMessage({ type: 'success', text: t('domains.verifySuccess') });
      } else {
        setMessage({ type: 'error', text: result.message || t('domains.verifyFail') });
      }
      await loadDomains();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('domains.verifyError') });
    } finally {
      setVerifying(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      active: { label: t('domains.statusActive'), cls: 'active' },
      pending_validation: { label: t('domains.statusPending'), cls: 'pending' },
      certificate_issued: { label: t('domains.statusCertIssued'), cls: 'issued' },
    };
    const s = map[status] || map.pending_validation;
    return <span className={`domains-status domains-status--${s.cls}`}>{s.label}</span>;
  };

  const copyToClipboard = (text, fieldKey) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setMessage({ type: 'success', text: t('domains.copied') });
    setTimeout(() => {
      setCopiedField(null);
      setMessage(null);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="domains-page">
        <div className="page-loading">{t('common.loading', 'Cargando...')}</div>
      </div>
    );
  }

  return (
    <div className="domains-page">
      <div className="domains-header">
        <h1>{t('domains.title')}</h1>
        <p className="domains-subtitle">{t('domains.subtitle')}</p>
      </div>

      {message && (
        <div className={`domains-message domains-message--${message.type}`}>
          <span className="material-icons">
            {message.type === 'success' ? 'check_circle' : 'error'}
          </span>
          {message.text}
        </div>
      )}

      <div className="domains-grid">
        {DOMAIN_TYPES.map((dtype) => {
          const domain = getDomainForType(dtype.id);

          return (
            <div key={dtype.id} className="domains-card">
              <div className="domains-card__header">
                <Icon name={dtype.icon} size={24} />
                <h2>{t(dtype.labelKey)}</h2>
                {domain && getStatusBadge(domain.status)}
              </div>

              {domain ? (
                <div className="domains-card__content">
                  <div className="domains-card__hostname">{domain.hostname}</div>

                  {/* Validation CNAME */}
                  {domain.status === 'pending_validation' && domain.validationCname && (
                    <div className="domains-dns-section">
                      <p className="domains-dns-title">{t('domains.validationStep')}</p>
                      <p className="domains-dns-hint">{t('domains.validationHint')}</p>
                      <div className="domains-dns-table">
                        <div className="domains-dns-table__header">
                          <span>{t('domains.recordType')}</span>
                          <span>{t('domains.recordName')}</span>
                          <span>{t('domains.recordValue')}</span>
                        </div>
                        <div className="domains-dns-table__row">
                          <span className="domains-dns-badge">CNAME</span>
                          <button
                            type="button"
                            className="domains-dns-copyable"
                            onClick={() =>
                              copyToClipboard(domain.validationCname.name, `${dtype.id}-val-name`)
                            }
                            title={
                              copiedField === `${dtype.id}-val-name`
                                ? t('domains.copied')
                                : t('domains.clickToCopy')
                            }
                            aria-label={t('domains.clickToCopy')}
                          >
                            <code>{domain.validationCname.name}</code>
                            <span
                              className={`material-icons domains-copy-icon${copiedField === `${dtype.id}-val-name` ? ' domains-copy-icon--copied' : ''}`}
                            >
                              {copiedField === `${dtype.id}-val-name` ? 'check' : 'content_copy'}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="domains-dns-copyable"
                            onClick={() =>
                              copyToClipboard(domain.validationCname.value, `${dtype.id}-val-value`)
                            }
                            title={
                              copiedField === `${dtype.id}-val-value`
                                ? t('domains.copied')
                                : t('domains.clickToCopy')
                            }
                            aria-label={t('domains.clickToCopy')}
                          >
                            <code>{domain.validationCname.value}</code>
                            <span
                              className={`material-icons domains-copy-icon${copiedField === `${dtype.id}-val-value` ? ' domains-copy-icon--copied' : ''}`}
                            >
                              {copiedField === `${dtype.id}-val-value` ? 'check' : 'content_copy'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Domain CNAME when active */}
                  {domain.status === 'active' && domain.distributionDomain && (
                    <div className="domains-dns-section domains-dns-section--success">
                      <p className="domains-dns-title">{t('domains.pointDomainStep')}</p>
                      <p className="domains-dns-hint">{t('domains.pointDomainHint')}</p>
                      <div className="domains-dns-table">
                        <div className="domains-dns-table__header">
                          <span>{t('domains.recordType')}</span>
                          <span>{t('domains.recordName')}</span>
                          <span>{t('domains.recordValue')}</span>
                        </div>
                        <div className="domains-dns-table__row">
                          <span className="domains-dns-badge">CNAME</span>
                          <button
                            type="button"
                            className="domains-dns-copyable"
                            onClick={() =>
                              copyToClipboard(domain.hostname, `${dtype.id}-dist-name`)
                            }
                            title={
                              copiedField === `${dtype.id}-dist-name`
                                ? t('domains.copied')
                                : t('domains.clickToCopy')
                            }
                            aria-label={t('domains.clickToCopy')}
                          >
                            <code>{domain.hostname}</code>
                            <span
                              className={`material-icons domains-copy-icon${copiedField === `${dtype.id}-dist-name` ? ' domains-copy-icon--copied' : ''}`}
                            >
                              {copiedField === `${dtype.id}-dist-name` ? 'check' : 'content_copy'}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="domains-dns-copyable"
                            onClick={() =>
                              copyToClipboard(domain.distributionDomain, `${dtype.id}-dist-value`)
                            }
                            title={
                              copiedField === `${dtype.id}-dist-value`
                                ? t('domains.copied')
                                : t('domains.clickToCopy')
                            }
                            aria-label={t('domains.clickToCopy')}
                          >
                            <code>{domain.distributionDomain}</code>
                            <span
                              className={`material-icons domains-copy-icon${copiedField === `${dtype.id}-dist-value` ? ' domains-copy-icon--copied' : ''}`}
                            >
                              {copiedField === `${dtype.id}-dist-value` ? 'check' : 'content_copy'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="domains-card__actions">
                    {domain.status !== 'active' && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleVerify(domain.hostname)}
                        disabled={verifying === domain.hostname}
                      >
                        {verifying === domain.hostname
                          ? t('domains.verifying')
                          : t('domains.verify')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => setRemoveConfirm(domain.hostname)}
                      aria-label={t('domains.removeDomainLabel', { hostname: domain.hostname })}
                      title={t('domains.removeDomainLabel', { hostname: domain.hostname })}
                    >
                      <Icon name="delete" size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="domains-card__empty">
                  <p className="domains-card__empty-text">{t('domains.noDomain')}</p>
                  <div className="domains-card__add">
                    <input
                      type="text"
                      value={newHostnames[dtype.id]}
                      onChange={(e) =>
                        setNewHostnames((prev) => ({ ...prev, [dtype.id]: e.target.value }))
                      }
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        !adding &&
                        newHostnames[dtype.id]?.trim() &&
                        handleAdd(dtype.id)
                      }
                      placeholder={t('domains.placeholder', { type: dtype.id })}
                      disabled={adding === dtype.id}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleAdd(dtype.id)}
                      disabled={adding === dtype.id || !newHostnames[dtype.id]?.trim()}
                    >
                      {adding === dtype.id ? t('domains.adding') : t('domains.add')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="domains-instructions">
        <h2>{t('domains.howItWorks')}</h2>
        <ol>
          <li>{t('domains.step1')}</li>
          <li>{t('domains.step2')}</li>
          <li>{t('domains.step3')}</li>
          <li>{t('domains.step4')}</li>
        </ol>
      </div>

      {removeConfirm && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-domain-title"
          >
            <div className="modal-header">
              <h2 id="remove-domain-title">{t('domains.removeConfirmTitle')}</h2>
              <button type="button" className="close-btn" onClick={() => setRemoveConfirm(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>{t('domains.confirmRemove', { hostname: removeConfirm })}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRemoveConfirm(null)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleRemove(removeConfirm)}
              >
                {t('domains.confirmRemoveAction')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DomainsPage;
